import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import heroKitchen from '../assets/hero-kitchen.png';

const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.saverly.app&hl=en_US';

const LandingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hasInviteParams =
      location.search.includes('__clerk_ticket') ||
      location.search.includes('__clerk_status');
    if (hasInviteParams) {
      navigate(`/accept-invitation${location.search}`, { replace: true });
    }
  }, [location.search, navigate]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="relative text-white overflow-hidden min-h-[60vh] lg:min-h-[65vh]"
        style={{ 
          backgroundImage: `url(${heroKitchen})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 35%',
          backgroundRepeat: 'no-repeat'
        }}
      >

        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 z-10">
          <div className="grid lg:grid-cols-[2fr_1fr] gap-12 items-center">
            {/* Hero Content with Semi-Transparent Background */}
            <div className="space-y-8">
              {/* Content Container with Glassmorphism Background */}
              <div className="bg-black/40 backdrop-blur-md rounded-2xl p-16 border border-white/20 shadow-2xl max-w-4xl w-full">
                <div className="space-y-6">
                  <div className="inline-block bg-white/30 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                    SAVERLY MEAL PLANNING
                  </div>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-poppins leading-tight text-white">
                    Sales-powered meal planning
                    <span className="block text-white">meets smart grocery shopping</span>
                  </h1>
                  <p className="text-xl sm:text-2xl text-white/95 font-inter leading-relaxed">
                    Browse sales from Kroger family stores, create complete meal plans with AI, and get matched products automatically. Save time and money on every grocery trip.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <a 
                    href="https://apps.apple.com/us/app/saverly-kroger-meals/id6756591268" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-8 py-4 bg-saverly-primary hover:bg-saverly-deep text-white text-lg font-semibold rounded-xl shadow-xl transition-all duration-200 transform hover:scale-105"
                  >
                    <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    Download iOS App
                  </a>
                  <a
                    href={GOOGLE_PLAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-8 py-4 border-2 border-white bg-white/5 backdrop-blur-sm text-white text-lg font-semibold rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105"
                  >
                    <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    Download Android App
                  </a>
                </div>
                
                {/* Feature Highlights */}
                <div className="flex items-center justify-center space-x-6 pt-8 mt-8 border-t border-white/20">
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">🛒 Browse Sales</div>
                    <div className="text-white/90 text-sm">Real Deals from Kroger Family Stores</div>
                  </div>
                  <div className="h-8 w-px bg-white/50"></div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">🛒 Sales-Based Meal Plans</div>
                    <div className="text-white/90 text-sm">Complete Recipes</div>
                  </div>
                  <div className="h-8 w-px bg-white/50"></div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">🎯 Auto-Matching</div>
                    <div className="text-white/90 text-sm">Smart Product Selection</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right column - empty space for visual balance */}
            <div className="hidden lg:block">
              {/* Beautiful kitchen image shows through here */}
            </div>

          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-saverly-dark font-poppins">
              Everything You Need for Smarter Grocery Shopping
            </h2>
            <p className="text-xl text-saverly-gray font-inter max-w-3xl mx-auto">
              From browsing store sales to generating complete meal plans with matched products—all in one powerful app.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center space-y-4 p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="w-16 h-16 bg-saverly-primary rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-saverly-dark font-poppins">Browse Live Sales</h3>
              <p className="text-saverly-gray font-inter">
                See real-time sales and deals from Kroger family stores. Filter by category, search products, and view detailed product information including stock levels and nutrition.
              </p>
            </div>
            
            {/* Feature 2 */}
            <div className="text-center space-y-4 p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="w-16 h-16 bg-saverly-primary rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-saverly-dark font-poppins">Sales-Powered Meal Planning</h3>
              <p className="text-saverly-gray font-inter">
                Generate complete meal plans from any product. Our AI-assisted process creates full recipes with sides, vegetables, and all ingredients—respecting your dietary preferences.
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="text-center space-y-4 p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200">
              <div className="w-16 h-16 bg-saverly-deep rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-saverly-dark font-poppins">Automatic Product Matching</h3>
              <p className="text-saverly-gray font-inter">
                Every ingredient is automatically matched to real products from Kroger family stores with intelligent scoring. Category filtering ensures you get the right items every time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-saverly-primary to-saverly-accent text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-poppins mb-6">
            Download Saverly Mobile App
          </h2>
          <p className="text-xl text-white/90 font-inter mb-8">
            Available on iOS and Android
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://apps.apple.com/us/app/saverly-kroger-meals/id6756591268" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-saverly-primary hover:bg-gray-100 text-lg font-semibold rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Download for iOS
            </a>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/20 hover:bg-white/30 text-white text-lg font-semibold rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              Download for Android
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage; 